// src/components/MainLayout.tsx
import React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  ArrowDownTrayIcon, ArrowRightEndOnRectangleIcon, ArrowRightStartOnRectangleIcon, Bars3Icon, HomeIcon, InformationCircleIcon, UserGroupIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../hooks/useAuth';
import { SyncStatusIndicator } from './SyncStatusIndicator';
import { UndoToast } from './UndoToast';
import { LocalDataOfferDialog } from './LocalDataOfferDialog';
import { ActionMenu, ActionMenuItem, Avatar, cx, focusRing } from './ui';

/** The pages with a place of their own in the navigation: the top bar on a wide screen, the tab
 * bar on a phone, where Dashboard reads "Today". */
const PRIMARY_NAV = [
  { label: 'Dashboard', shortLabel: 'Today', path: '/dashboard', icon: HomeIcon },
  { label: 'Households', shortLabel: 'Households', path: '/households', icon: UserGroupIcon },
];

export const MainLayout: React.FC = () => {
  const { user, signOut, isLocal } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/dashboard');
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  // In local mode, the local user's name (LOCAL_DISPLAY_NAME).
  const userName = user?.displayName || user?.email || 'You';

  // The rest, behind the avatar on a wide screen and behind More on a phone.
  const secondaryItems: ActionMenuItem[] = [
    { label: 'About Chores Champ', icon: InformationCircleIcon, onClick: () => navigate('/about') },
    { label: 'Import from Google Tasks', icon: ArrowDownTrayIcon, onClick: () => navigate('/import') },
    'separator',
    isLocal
      ? { label: 'Sign in to sync', icon: ArrowRightEndOnRectangleIcon, onClick: () => navigate('/login') }
      : { label: 'Sign out', icon: ArrowRightStartOnRectangleIcon, onClick: handleSignOut },
  ];

  const signedInAs = (
    <>
      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
        {isLocal ? 'On this phone only' : user?.displayName || 'Signed in'}
      </p>
      {isLocal && <p className="truncate text-xs text-gray-500 dark:text-gray-400">Not signed in</p>}
      {!isLocal && user?.email && <p className="truncate text-xs text-gray-500 dark:text-gray-400">{user.email}</p>}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 pt-[env(safe-area-inset-top)] backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:border-gray-800 dark:bg-gray-900/95 dark:supports-[backdrop-filter]:bg-gray-900/80">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-6 px-4 sm:px-6 lg:px-8">
          <Link to="/dashboard" className={cx('flex items-center gap-2.5 rounded-lg', focusRing)}>
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 text-white shadow-sm dark:shadow-none">
              <CheckIcon className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="text-base font-bold tracking-tight text-gray-900 dark:text-gray-100">Chores Champ</span>
          </Link>

          <nav aria-label="Main navigation" className="hidden items-center gap-1 sm:flex">
            {PRIMARY_NAV.map(({ label, path, icon: Icon }) => (
              <NavLink
                key={path}
                to={path}
                className={({ isActive }) => cx(
                  'flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors',
                  focusRing,
                  isActive
                    ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100',
                )}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <SyncStatusIndicator />
            <ActionMenu
              label={`Account menu for ${userName}`}
              className="hidden sm:block"
              triggerClassName="rounded-full"
              trigger={<Avatar name={userName} id={user?.uid} size="md" />}
              header={signedInAs}
              items={secondaryItems}
            />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-6 sm:px-6 sm:pb-12 sm:pt-8 lg:px-8">
        <Outlet />
      </main>

      {/* Tab bar, on a phone only: within reach of the thumb. */}
      <nav
        aria-label="Main navigation"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:border-gray-800 dark:bg-gray-900/95 dark:supports-[backdrop-filter]:bg-gray-900/80 sm:hidden"
      >
        <div className="grid h-16 grid-cols-3">
          {PRIMARY_NAV.map(({ shortLabel, path, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) => cx(
                'flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium',
                'focus-visible:outline-none focus-visible:bg-gray-100 dark:focus-visible:bg-gray-800',
                isActive ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-500 dark:text-gray-400',
              )}
            >
              <Icon className="h-6 w-6" aria-hidden="true" />
              {shortLabel}
            </NavLink>
          ))}
          <ActionMenu
            label="More"
            anchor="top end"
            className="flex"
            triggerClassName="flex w-full flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-gray-500 data-[open]:text-indigo-700 focus-visible:bg-gray-100 dark:text-gray-400 dark:data-[open]:text-indigo-300 dark:focus-visible:bg-gray-800"
            trigger={<><Bars3Icon className="h-6 w-6" aria-hidden="true" />More</>}
            header={signedInAs}
            items={secondaryItems}
          />
        </div>
      </nav>

      <UndoToast />
      <LocalDataOfferDialog />
    </div>
  );
};
